'use client';

/**
 * The three email/calendar sections of chat settings, all driven by one
 * `useEmailSendPreferences` state object and one Save button.
 *
 * The calendar choice is the same primary-vs-secondary Google account as
 * CRM → Unified Calendar — deliberately NOT the Nango email sender above it.
 */

import React from 'react';
import { connectionValue, getProviderLabel } from './email-send-context';
import type { EmailSendPreferences } from './useEmailSendPreferences';

const SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export function DefaultEmailAccountSection({ prefs }: { prefs: EmailSendPreferences }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">Default email account</h3>
      <p className="text-xs text-muted-foreground mb-3">
        When you send an email from chat, this account is used unless you pick another in the
        approval step.
      </p>
      {prefs.loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : prefs.connections.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No email connections found. Connect an account in your email settings to choose a
          default.
        </p>
      ) : (
        <>
          <select
            value={prefs.selectedValue}
            onChange={(e) => prefs.setSelectedValue(e.target.value)}
            className={SELECT_CLASS}
            aria-label="Default email account"
          >
            <option value="">No default</option>
            {prefs.connections.map((c) => (
              <option key={c.connectionId} value={connectionValue(c)}>
                {c.email
                  ? `${c.email} (${getProviderLabel(c.provider)})`
                  : `${c.connectionId} (${getProviderLabel(c.provider)})`}
              </option>
            ))}
          </select>
          {prefs.error && <p className="text-sm text-destructive mt-2">{prefs.error}</p>}
          {prefs.saved && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">Saved.</p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={prefs.save}
              disabled={prefs.saving || !prefs.hasChanges}
              className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
            >
              {prefs.saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function MeridianCalendarSection({ prefs }: { prefs: EmailSendPreferences }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">
        Google Calendar for Meridian
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Which linked Google Calendar account to use when you list events, view invites, or create
        meetings from chat. This is the same primary vs secondary choice as CRM → Unified Calendar
        (not your Nango email sender above). If your default sender is Gmail, choose Primary below
        so Meet and invites usually match that mailbox.
      </p>
      {prefs.loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="meridian-calendar-account"
              checked={!prefs.calendarUseSecondAccount}
              onChange={() => prefs.setCalendarUseSecondAccount(false)}
              className="rounded-full border-input"
            />
            <span>Primary linked Google Calendar</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="meridian-calendar-account"
              checked={prefs.calendarUseSecondAccount}
              onChange={() => prefs.setCalendarUseSecondAccount(true)}
              className="rounded-full border-input"
            />
            <span>Secondary linked Google Calendar</span>
          </label>
        </div>
      )}
    </section>
  );
}

export function DefaultSignatureSection({ prefs }: { prefs: EmailSendPreferences }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">Default signature</h3>
      <p className="text-xs text-muted-foreground mb-3">
        When you send an email from chat using the default account above, this signature is
        appended unless you pick another in the approval step.
      </p>
      {prefs.loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !prefs.parsed?.connectionId ? (
        <p className="text-sm text-muted-foreground">
          Select a default email account above to set a default signature for it.
        </p>
      ) : prefs.signaturesForConnection.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No signatures for this connection. Add signatures in email signatures settings to choose
          a default.
        </p>
      ) : (
        <select
          value={prefs.selectedSignatureId}
          onChange={(e) => prefs.setSelectedSignatureId(e.target.value)}
          className={SELECT_CLASS}
          aria-label="Default signature for this account"
        >
          <option value="">No default</option>
          {prefs.signaturesForConnection.map((s) => (
            <option key={s.id} value={s.id ?? ''}>
              {s.name ?? s.id} {s.is_default ? '(current default)' : ''}
            </option>
          ))}
        </select>
      )}
    </section>
  );
}
